import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {DeviceReportTableComponent} from '../device-report-table/device-report-table.component';
import {ReportsService} from '../../../../../data/reports/reports.service';
import moment, {Moment} from 'moment';
import {ConfigurationModel} from '../../../../../shared/models/configuration.model';
import {ToastService} from '../../../../../shared/services/toast.service';
import {Subscription} from 'rxjs';
import {ConfigurationService} from '../../../../../configuration/configuration.service';
import {DeviceReportRecordModel} from '../../../../../shared/models/device-report.model';
import {ActivatedRoute, Router} from '@angular/router';
import {CloseableAndFilterableInsight} from '../../../closeable-filterable-insight.class';
import { FilterButtonComponent } from '../../../../../shared/components/buttons/filter-button/filter-button.component';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { TextSearchCompositeComponent } from '../../../../../shared/components/filter-bar/text-search-composite/text-search-composite';
import { NgClass } from '@angular/common';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import {saveAs} from 'file-saver-es';
import { DeviceReportColumn } from '../DeviceReportColumn';
import { MatDialog } from '@angular/material/dialog';
import {
  DeviceReportSettingsDialogComponent,
  DeviceReportSettingsDialogData,
} from '../device-report-settings-dialog/device-report-settings-dialog.component';
import { FilterBarComponent, FilterBarSearchEvent } from '../../../../../shared/components/filter-bar/filter-bar.component';
import { DateFilter } from '../../../../../shared/models/DateFilter';

@Component({
  selector: 'app-device-report',
  templateUrl: './device-report.component.html',
  styleUrls: ['./device-report.component.scss'],
  imports: [
    FilterButtonComponent,
    MatIconButton,
    MatTooltip,
    MatIcon,
    DeviceReportTableComponent,
    TextSearchCompositeComponent,
    NgClass,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FilterBarComponent,
  ],
})
export class DeviceReportComponent extends CloseableAndFilterableInsight implements AfterViewInit, OnInit, OnDestroy {
  configuration: ConfigurationModel;
  @Input() isBrowserMobile?: boolean;

  pageSize = 50;
  pageIndex = 0;
  totalElements = 0;
  private searchCriteria: {};
  private dateFilter: DateFilter = undefined;

  @ViewChild(DeviceReportTableComponent) reportTableComponent: DeviceReportTableComponent;

  dataSource: MatTableDataSource<DeviceReportRecordModel> = new MatTableDataSource([]);
  isLoading = false;
  isScrollLoading = false;
  columns: DeviceReportColumn[];

  private configurationSubscription: Subscription;

  constructor(
    private readonly reportService: ReportsService,
    private readonly configurationService: ConfigurationService,
    protected readonly router: Router,
    protected readonly activatedRoute: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly toast: ToastService
  ) {
    super(router, activatedRoute);
  }

  ngOnInit() {
    this.configurationSubscription = this.configurationService.sharedConfigurationModel.subscribe(model => {
      if (model) {
        this.configuration = model;
      }
    });

    // store and use default display columns setting if needed
    let displayedColumnsSetting = localStorage.getItem(DeviceReportColumn.LOCAL_STORAGE_DISPLAYED_COLUMNS);
    if (!displayedColumnsSetting) {
      displayedColumnsSetting = DeviceReportColumn.displayedByDefault.join(',');
      localStorage.setItem(DeviceReportColumn.LOCAL_STORAGE_DISPLAYED_COLUMNS, displayedColumnsSetting);
    }
    this.columns = DeviceReportColumn.columns(displayedColumnsSetting.split(','));
  }

  ngAfterViewInit() {
    // not needed, filters will do the job
    this.loadReport();
  }

  ngOnDestroy() {
    this.configurationSubscription?.unsubscribe();
  }

  toggleLoading() {
    setTimeout(() => (this.isLoading = !this.isLoading));
  }

  // null or non-empty array
  loadReport() {
    if (!!this.dateFilter && !!this.reportTableComponent) {
      this.toggleLoading();
      this.pageIndex = 0;
      this.isScrollLoading = false;
      this.reportService
        .getDeviceReport(
          this.dateFilter.from ? this.dateFilter.from : null,
          this.dateFilter.to ? this.dateFilter.to : null,
          this.searchCriteria,
          this.pageIndex,
          this.pageSize,
          this.reportTableComponent.getSort(),
        )
        .then(response => {
          // console.log(`initialized, load count ${response.data.records.content.length} of ${response.data.records.totalElements}`);
          this.dataSource.data = response.records.content;
          this.totalElements = response.records.totalElements;
          this.pageSize = response.records.pageable.pageSize;
        })
        .catch(error => {
          this.toast.longer('Error while fetching initial data from the server');
          console.error(error);
        })
        .finally(() => {
          this.toggleLoading();
        });
    }
  }

  extendReport() {
    if (!this.isScrollLoading && this.dataSource.data.length < this.totalElements) {
      this.pageIndex++;
      this.isScrollLoading = true;
      this.reportService
        .getDeviceReport(
          this.dateFilter.from ? this.dateFilter.from : null,
          this.dateFilter.to ? this.dateFilter.to : null,
          this.searchCriteria,
          this.pageIndex,
          this.pageSize,
          this.reportTableComponent.getSort(),
        )
        .then(response => {
          const extendedData = [...this.dataSource.data];
          extendedData.push(...response.records.content);
          this.dataSource.data = extendedData;
        })
        .catch(error => {
          this.toast.longer('Error while fetching initial data from the server');
          console.error(error);
        })
        .finally(() => {
          this.isScrollLoading = false;
        });
    }
  }

  loadCSVExportFile() {
    if (this.dataSource.data?.length && !this.isLoading) {
      this.toggleLoading();
      this.reportService
        .getDeviceReportCSV(
          this.dateFilter.from ? this.dateFilter.from : null,
          this.dateFilter.to ? this.dateFilter.to : null,
          this.searchCriteria,
          this.reportTableComponent.getSort()
        )
        .then(response => {
          const blob = new Blob([response], { type: 'csv' });
          saveAs(
            blob,
            `${this._fileNameWithDateRange(
              this.dateFilter.from ? this.dateFilter.from : null,
              this.dateFilter.to ? this.dateFilter.to : null
            )}.csv`
          );
        })
        .catch(error => {
          this.toast.longer('Error while fetching data from the server');
          console.error(error);
        })
        .finally(() => this.toggleLoading());
    }
  }

  private _fileNameWithDateRange(from: Moment, to: Moment): string {
    const fromStr = !!from ? `_${from.format('MM-DD-YYYY')}` : '';
    const toStr = !!to ? to.format('MM-DD-YYYY') : `${moment().format('MM-DD-YYYY')}`;
    return `device_report${fromStr}_${toStr}`;
  }

  dateFilterChanged(dateFilter: DateFilter) {
    this.dateFilter = dateFilter;
    this.loadReport();
  }

  textFiltersChanged(events: FilterBarSearchEvent[]) {
    this.searchCriteria = {};
    events.forEach(event => this.searchCriteria[event.id] = event.search);
    this.loadReport();
  }

  showSettingsDialog() {
    const dialogRef = this.dialog.open(DeviceReportSettingsDialogComponent, {
      data: {
        columns: this.columns.map(value => ({ ...value })),
      } as DeviceReportSettingsDialogData,
      width: '300px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result != null) {
        const data = result as DeviceReportSettingsDialogData;
        localStorage.setItem(
          DeviceReportColumn.LOCAL_STORAGE_DISPLAYED_COLUMNS,
          data.columns
            .filter(value => value.displayed)
            .map(value => value.id)
            .join(',')
        );
        this.columns = data.columns;
      }
    });
  }
}
